import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TrashIcon, PhotoIcon, ArrowUpTrayIcon, Bars3Icon, PlusIcon } from '@heroicons/react/24/outline';
import { homepageService } from '../services/homepage';
import { HomepageBanner } from '../types/homepage';
import { useToast } from '../contexts/ToastContext';
import ImageCropModal from '../components/ImageCropModal';

const HomepageBanners: React.FC = () => {
  const [banners, setBanners] = useState<HomepageBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedBanner, setDraggedBanner] = useState<string | null>(null);
  const [dragOverBanner, setDragOverBanner] = useState<string | null>(null);
  const [filesToCrop, setFilesToCrop] = useState<File[]>([]);
  const [currentCropIndex, setCurrentCropIndex] = useState(0);
  const [currentCropSrc, setCurrentCropSrc] = useState<string | null>(null);
  const [croppedFiles, setCroppedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      setLoading(true);
      const data = await homepageService.getBanners();
      setBanners(data);
    } catch (err: any) {
      console.error('Failed to load banners:', err);
      showError('Failed to load banners');
    } finally {
      setLoading(false);
    }
  };

  const startCropFlow = (files: File[]) => {
    const validFiles = files.filter(file => {
      const error = validateImageFile(file);
      if (error) {
        showError(`${file.name}: ${error}`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setFilesToCrop(validFiles);
    setCroppedFiles([]);
    setCurrentCropIndex(0);
    const reader = new FileReader();
    reader.onloadend = () => setCurrentCropSrc(reader.result as string);
    reader.readAsDataURL(validFiles[0]);
  };

  const handleBannerCropComplete = (croppedBlob: Blob) => {
    const file = new File([croppedBlob], `banner-${currentCropIndex}.jpg`, { type: 'image/jpeg' });
    const newCropped = [...croppedFiles, file];

    if (currentCropIndex + 1 < filesToCrop.length) {
      setCroppedFiles(newCropped);
      const nextIndex = currentCropIndex + 1;
      setCurrentCropIndex(nextIndex);
      const reader = new FileReader();
      reader.onloadend = () => setCurrentCropSrc(reader.result as string);
      reader.readAsDataURL(filesToCrop[nextIndex]);
    } else {
      setFilesToCrop([]);
      setCroppedFiles([]);
      setCurrentCropSrc(null);
      uploadImages(newCropped);
    }
  };

  const handleBannerCropCancel = () => {
    setFilesToCrop([]);
    setCroppedFiles([]);
    setCurrentCropSrc(null);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    startCropFlow(Array.from(files));

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateImageFile = (file: File): string | null => {
    if (!file.type.startsWith('image/')) {
      return 'Please select an image file';
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return 'File size must be less than 10MB';
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return 'Please select a valid image format (JPEG, PNG, WebP)';
    }

    return null;
  };

  const uploadImages = async (files: File[]) => {
    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const file of files) {
      const error = validateImageFile(file);
      if (error) {
        showError(`${file.name}: ${error}`);
        errorCount++;
        continue;
      }

      try {
        const bannerData = {
          isActive: true,
          mediaType: 'image' as 'image',
        };

        await homepageService.createBanner(bannerData, file);
        successCount++;
      } catch (err: any) {
        console.error('Failed to upload image:', err);
        showError(`Failed to upload ${file.name}: ${err.response?.data?.error || 'Unknown error'}`);
        errorCount++;
      }
    }

    if (successCount > 0) {
      showSuccess(`${successCount} image${successCount > 1 ? 's' : ''} uploaded successfully`);
      await loadBanners();
    }

    setUploading(false);
  };

  const handleDelete = async (bannerId: string) => {
    if (!window.confirm('Are you sure you want to delete this banner? The image will be permanently removed.')) {
      return;
    }

    try {
      await homepageService.deleteBanner(bannerId);
      setBanners(banners.filter(b => b.id !== bannerId));
      showSuccess('Banner deleted successfully');
    } catch (err: any) {
      console.error('Failed to delete banner:', err);
      showError('Failed to delete banner');
    }
  };

  const handleToggleStatus = async (bannerId: string, currentStatus: boolean) => {
    try {
      const newStatus = !currentStatus;
      await homepageService.updateBanner(bannerId, { isActive: newStatus });
      setBanners(banners.map(b =>
        b.id === bannerId ? { ...b, isActive: newStatus } : b
      ));
      showSuccess(`Banner ${newStatus ? 'enabled' : 'disabled'} successfully`);
    } catch (err: any) {
      console.error('Failed to update banner status:', err);
      showError('Failed to update banner status');
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files).filter(file =>
      file.type.startsWith('image/')
    );

    if (files.length === 0) {
      showError('Please drop image files only');
      return;
    }

    startCropFlow(files);
  };

  // Banner reordering via drag and drop
  const handleBannerDragStart = (e: React.DragEvent<HTMLDivElement>, bannerId: string) => {
    setDraggedBanner(bannerId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleBannerDragOver = (e: React.DragEvent<HTMLDivElement>, bannerId: string) => {
    e.preventDefault();
    if (draggedBanner && draggedBanner !== bannerId) {
      setDragOverBanner(bannerId);
    }
  };

  const handleBannerDragLeave = () => {
    setDragOverBanner(null);
  };

  const handleBannerDrop = async (e: React.DragEvent<HTMLDivElement>, targetBannerId: string) => {
    e.preventDefault();

    if (!draggedBanner || draggedBanner === targetBannerId) {
      setDraggedBanner(null);
      setDragOverBanner(null);
      return;
    }

    const draggedIndex = banners.findIndex(b => b.id === draggedBanner);
    const targetIndex = banners.findIndex(b => b.id === targetBannerId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Reorder locally first
    const newBanners = [...banners];
    const [removed] = newBanners.splice(draggedIndex, 1);
    newBanners.splice(targetIndex, 0, removed);

    // Update display order
    const updatedBanners = newBanners.map((banner, index) => ({
      ...banner,
      displayOrder: index
    }));

    setBanners(updatedBanners);
    setDraggedBanner(null);
    setDragOverBanner(null);

    // Save order to server
    try {
      await homepageService.updateBannerOrder(
        updatedBanners.map(b => ({ id: b.id, displayOrder: b.displayOrder }))
      );
      showSuccess('Banner order updated');
    } catch (err: any) {
      console.error('Failed to update banner order:', err);
      showError('Failed to update banner order');
      await loadBanners(); // Reload original order
    }
  };

  const handleBannerDragEnd = () => {
    setDraggedBanner(null);
    setDragOverBanner(null);
  };

  const getImageUrl = useCallback((banner: HomepageBanner): string | null => {
    if (banner.s3Url) return banner.s3Url;
    if (banner.imagePath) return homepageService.getImageUrl(banner.imagePath) || null;
    return null;
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Homepage Banners</h1>
          <p className="mt-2 text-sm text-gray-700">
            Upload images to display as a rotating banner on the homepage. Drag to reorder. Images will be cropped to 16:9 aspect ratio (2000×1125px).
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-4">
          <button
            onClick={handleUploadClick}
            disabled={uploading}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Banner
          </button>
        </div>
      </div>

      {/* Drop Zone for new images */}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`mb-6 p-8 rounded-lg border-2 border-dashed transition-all ${
          isDragging
            ? 'border-indigo-500 bg-indigo-50 border-solid'
            : 'bg-gray-50 border-gray-300 hover:border-gray-400'
        }`}
      >
        <div className="text-center">
          <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm font-medium text-gray-900">
            {uploading ? 'Uploading...' : 'Drop images here or click to upload'}
          </p>
          <p className="mt-1 text-xs text-gray-500">JPEG, PNG, WebP up to 10MB each</p>
          <button
            onClick={handleUploadClick}
            disabled={uploading}
            className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
            {uploading ? 'Uploading...' : 'Select Images'}
          </button>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
        disabled={uploading}
        multiple
      />

      {/* Banner List */}
      {banners.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No banners</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by uploading your first banner image.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-500 mb-4">
            Drag banners to reorder them. The order shown here is how they will appear on the homepage.
          </p>
          {banners.map((banner, index) => {
            const imageUrl = getImageUrl(banner);
            return (
              <div
                key={banner.id}
                draggable
                onDragStart={(e) => handleBannerDragStart(e, banner.id)}
                onDragOver={(e) => handleBannerDragOver(e, banner.id)}
                onDragLeave={handleBannerDragLeave}
                onDrop={(e) => handleBannerDrop(e, banner.id)}
                onDragEnd={handleBannerDragEnd}
                className={`bg-white rounded-lg shadow-sm border overflow-hidden transition-all cursor-move ${
                  draggedBanner === banner.id ? 'opacity-50' : ''
                } ${
                  dragOverBanner === banner.id ? 'border-indigo-500 border-2' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start p-4">
                  {/* Drag Handle */}
                  <div className="flex-shrink-0 mr-4 text-gray-400 hover:text-gray-600">
                    <Bars3Icon className="h-6 w-6" />
                  </div>

                  {/* Image Preview */}
                  <div className="flex-shrink-0 w-48 h-28 bg-gray-100 rounded-lg overflow-hidden mr-4">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={banner.title || `Banner ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <PhotoIcon className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Banner Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mr-2">
                        #{index + 1}
                      </span>
                      {banner.isActive ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          Inactive
                        </span>
                      )}
                    </div>
                    {banner.title && (
                      <p className="mt-1 text-sm font-medium text-gray-900 truncate">
                        {banner.title}
                      </p>
                    )}
                    {banner.subtitle && (
                      <p className="mt-1 text-sm text-gray-500 truncate">
                        {banner.subtitle}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-gray-400">
                      Added {new Date(banner.createdAt).toLocaleDateString()}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex-shrink-0 ml-4 flex items-center space-x-3">
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={banner.isActive}
                        onChange={() => handleToggleStatus(banner.id, banner.isActive)}
                        className="sr-only"
                      />
                      <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        banner.isActive ? 'bg-indigo-600' : 'bg-gray-200'
                      }`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          banner.isActive ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </div>
                    </label>
                    <button
                      onClick={() => handleDelete(banner.id)}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      title="Delete banner"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {currentCropSrc && (
        <ImageCropModal
          imageSrc={currentCropSrc}
          aspectRatio={16 / 9}
          title={`Crop Banner Image${filesToCrop.length > 1 ? ` (${currentCropIndex + 1} of ${filesToCrop.length})` : ''}`}
          outputWidth={2000}
          outputHeight={1125}
          onCropComplete={handleBannerCropComplete}
          onCancel={handleBannerCropCancel}
        />
      )}
    </div>
  );
};

export default HomepageBanners;
